/**
 * The tunables the *departed* flight regimes run on — the spiral dive and the
 * spin, both `spin.ts`'s business — split out of `tunables.ts` the same way
 * `gear-tunables.ts` and `gunnery-tunables.ts` were. They merge straight back
 * into `DEFAULT_TUNABLES`, so nothing downstream sees more than one table.
 *
 * Almost every key here is CALIBRATION-PENDING, and for one reason: the
 * 2026-08-19 DOSBox session never got the original to depart. "No spin
 * developed in any run, and no spin was provoked in level or climbing flight"
 * (re-sessions/calib/NOTES.md §4.5) — a full-back zoom sags its nose and flies
 * out. The two spiral *bias* rates are the exceptions, bounded from above by
 * that session's aileron and pitch traces rather than fitted to them; their
 * own comments say by how much.
 */

export const STALL_TUNABLES = {
  /** Bank beyond which the coordinated-turn requirement starts checking back-pressure (Intermediate and Expert). CALIBRATION-PENDING. */
  spiralBankThresholdRad: 0.35,
  /** Fraction of the load factor needed to hold the turn, below which it starts degenerating. CALIBRATION-PENDING. */
  spiralLoadFactorMargin: 0.85,
  /** Seconds of uncoordinated bank before the spiral is fully developed. CALIBRATION-PENDING. */
  spiralOnsetSeconds: 2,
  /** Roll-into-the-turn bias at full spiral severity. Bounded by measurement: full aileron from level in the original rolls at a flat 47–51 °/s past 227° of bank with no upward trend, so any added bias is under ~2 °/s. */
  spiralRollBiasRadPerSec: 0.03,
  /** Nose-drop bias at full spiral severity. Bounded by measurement: the same run's pitch fell only 4.3° over 5.4 s, most of which the lift-vector tilt explains. Kept small and non-zero so the manual's "slow spiral dive" still has a nudge. */
  spiralPitchBiasRadPerSec: 0.05,

  /**
   * Sideslip needed to break a spin-class-30 aircraft into autorotation; scaled
   * linearly by the class (flight-tunables.md §4.2).
   *
   * **Bracketed by measurement as of AD-007m** — no longer a free
   * CALIBRATION-PENDING guess, though still not a direct reading. AD-007m gave
   * every rotary a measured pitch→yaw coupling, and a coupling of `0.36·q`
   * against §8.5.3's measured alignment gain of 2.2 /s settles at a steady
   * `r/k ≈ 3.4°` of slip on *any* full-back pull, which the pilot never asked
   * for. At the old 0.12 rad (6.9° on a Camel) that free slip is most of the
   * threshold, and the Camel departed into a spin 3.0 s into the very banked
   * pull §4.4 measured its 173° swing on — where the original, which has the
   * same precession, flew it for 5.3 s and §4.5 recorded "no spin developed in
   * any run".
   *
   * Two-sided, and both sides are measured behaviours rather than opinions:
   * **below 0.15** the Camel spins out of that pull and out of §4.4's own
   * manoeuvre; **above 0.18** §4.5's stated entry — "an asymmetric entry, rudder
   * held at the stall" — stops working, and six spin-capable types can no longer
   * be provoked into a spin at all. 0.165 is the middle of that window. The
   * wings-level zoom of §4.5 does not spin at any value in it.
   */
  spinEntryBetaRad: 0.165,
  /**
   * The skid a spin entry has to be made of, as a **lateral speed** rather than
   * an angle — the floor under `spinEntryBetaRad`, and the thing that stops a
   * stationary aeroplane from spinning.
   *
   * **MEASURED-DOSBOX (AD-007o), and it is an artefact fix rather than a new
   * mechanism.** `β = asin(vₓ/V)` is a *direction*, so its noise grows as `1/V`:
   * at the top of a full-back zoom the port hangs at 2–6 mph and 83° of pitch,
   * where a third of a metre per second of lateral drift reads as **9–15° of
   * "sideslip"** and trips the entry threshold. Measured on this branch, that is
   * exactly what five airframes were doing — Camel at 4.1 mph, Nieuport 17 at
   * 2.2, S.S.W. at 5.9, all of them at 83–85° of pitch with `|vₓ|` between
   * **0.26 and 0.42 m/s**.
   *
   * The original flies the same manoeuvre and does not spin. Session 1 §4.5
   * recorded "no spin developed in any run", and the AD-007o re-fly held full
   * back stick for 12.5 s of guest time and watched the Camel *loop* — over the
   * top at 5.8 mph, down the far side to 125 mph, and up into a second loop —
   * with no autorotation at any point.
   *
   * 2.0 m/s separates the two cases by an order of magnitude and is not a fitted
   * number: the artefact sits at 0.26–0.42 m/s, and §4.5's own stated entry —
   * "an asymmetric entry, rudder held at the stall" — puts **4.6 m/s** across
   * the wing at a 50 mph stall with the 0.165 rad threshold just met. Anything
   * between about 1 and 4 leaves both facts intact; the middle of that window is
   * where this sits. CALIBRATION-PENDING within it.
   */
  spinEntryMinSideslipMps: 2,
  /** Spin-class value treated as the reference for the entry threshold — 30 is "spins very easily" (Camel, Dr.I, SSW, Triplane). */
  spinClassReference: 30,
  /** Angle of attack autorotation locks onto. CALIBRATION-PENDING. */
  spinAlphaTargetRad: 0.6,
  /** How hard the spin holds that angle of attack, per radian of error. CALIBRATION-PENDING. */
  spinAlphaHoldGain: 3,
  /** Yaw rate in a developed spin. CALIBRATION-PENDING. */
  spinYawRateRadPerSec: 1.6,
  /** Roll rate in a developed spin. CALIBRATION-PENDING. */
  spinRollRateRadPerSec: 1.2,
  /** Continuous seconds of correct recovery input needed to break autorotation. CALIBRATION-PENDING. */
  spinRecoverySeconds: 1.5,
  /** Grace period after a successful recovery during which autorotation cannot restart. CALIBRATION-PENDING. */
  spinRecoveryLockoutSeconds: 4,
  /** Minimum opposite-rudder deflection that counts as recovery input. CALIBRATION-PENDING. */
  spinRecoveryRudderInput: 0.5,
  /** Stick position (negative = forward) at or below which the elevator counts as recovery input. CALIBRATION-PENDING. */
  spinRecoveryStickInput: -0.25,
} satisfies Record<string, number>;

export type StallTunableKey = keyof typeof STALL_TUNABLES;

/**
 * All but `spinClassReference`, which is a `.DAT` field value the data states
 * outright ("spins very easily" = 30), not a quantity anyone has to calibrate.
 */
export const STALL_TUNABLE_KEYS: readonly StallTunableKey[] = (
  Object.keys(STALL_TUNABLES) as StallTunableKey[]
).filter((key) => key !== "spinClassReference");
